/**
 * Reine Rechnung der Kanal-Zeitleiste: Monatsfenster, Balkenlage in Prozent und Spuren für Regeln,
 * die sich im selben Kanal überschneiden. Ohne React, damit die Geometrie prüfbar bleibt.
 */
import type { ChannelCloseoutListItem } from '@/shared/api';

export interface TimelineMonth {
  start: string;
  label: string;
  days: number;
}

export interface TimelineWindow {
  from: string;
  to: string;
  months: TimelineMonth[];
  totalDays: number;
}

export interface TimelineBar {
  rule: ChannelCloseoutListItem;
  left: number;
  width: number;
  lane: number;
}

const DAY_MS = 86_400_000;
const parse = (iso: string) => new Date(`${iso}T00:00:00Z`);
const format = (date: Date) => date.toISOString().slice(0, 10);
// Feste Kürzel statt Intl: Browser liefern „Sep" oder „Sept." je nach Umgebung.
const MONTHS = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

export function monthStart(iso: string) {
  return `${iso.slice(0, 7)}-01`;
}

export function shiftMonths(monthIso: string, delta: number) {
  const date = parse(monthStart(monthIso));
  date.setUTCMonth(date.getUTCMonth() + delta);
  return format(date);
}

/** „Okt 26" */
export function monthLabel(iso: string) {
  return `${MONTHS[Number(iso.slice(5, 7)) - 1]} ${iso.slice(2, 4)}`;
}

/** Zwölf Monate ab dem Monat von `anchor`; Januar trägt das Jahr, damit der Wechsel lesbar bleibt. */
export function timelineWindow(anchor: string, monthCount = 12): TimelineWindow {
  const months = Array.from({ length: monthCount }, (_, index) => {
    const start = shiftMonths(anchor, index);
    const next = parse(shiftMonths(anchor, index + 1));
    const date = parse(start);
    const label =
      date.getUTCMonth() === 0 || index === 0 ? monthLabel(start) : MONTHS[date.getUTCMonth()]!;
    return { start, label, days: Math.round((next.getTime() - date.getTime()) / DAY_MS) };
  });
  const to = format(new Date(parse(shiftMonths(anchor, monthCount)).getTime() - DAY_MS));
  return {
    from: months[0]!.start,
    to,
    months,
    totalDays: months.reduce((sum, month) => sum + month.days, 0),
  };
}

function offsetDays(window: TimelineWindow, iso: string) {
  return Math.round((parse(iso).getTime() - parse(window.from).getTime()) / DAY_MS);
}

/** Lage eines Tageszeitraums (beide Enden einschließlich), auf das Fenster zugeschnitten. */
export function barGeometry(window: TimelineWindow, start: string, end: string) {
  const first = Math.max(0, offsetDays(window, start));
  const last = Math.min(window.totalDays - 1, offsetDays(window, end));
  if (last < first) return null;
  return {
    left: (first / window.totalDays) * 100,
    width: ((last - first + 1) / window.totalDays) * 100,
  };
}

export function todayOffset(window: TimelineWindow, today: string) {
  const days = offsetDays(window, today);
  return days < 0 || days >= window.totalDays ? null : (days / window.totalDays) * 100;
}

/** Regeln eines Kanals mit Spur: überschneiden sich zwei, liegt die spätere eine Spur tiefer. */
export function channelBars(
  window: TimelineWindow,
  rules: readonly ChannelCloseoutListItem[],
  channel: string,
) {
  const laneEnds: string[] = [];
  const bars: TimelineBar[] = [];
  for (const rule of [...rules]
    .filter((item) => item.channels.includes(channel))
    .sort((left, right) => left.startDate.localeCompare(right.startDate))) {
    const geometry = barGeometry(window, rule.startDate, rule.endDate);
    if (!geometry) continue;
    const lane = laneEnds.findIndex((end) => end < rule.startDate);
    const assigned = lane === -1 ? laneEnds.length : lane;
    laneEnds[assigned] = rule.endDate;
    bars.push({ rule, ...geometry, lane: assigned });
  }
  return { bars, lanes: Math.max(1, laneEnds.length) };
}
